class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (event) => {
      const reviewToggle = event.target.closest?.('[data-cart-review-toggle]');
      if (reviewToggle) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleReviewCarousel(reviewToggle);
        return;
      }

      const reviewSlide = event.target.closest?.('[data-cart-review-expand]');
      if (reviewSlide) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleReviewExpansion(reviewSlide);
        return;
      }

      const recommendationButton = event.target.closest?.('[data-cart-recommendation-add]');
      if (recommendationButton) this.addRecommendedProduct(event, recommendationButton);
    });

    this.addEventListener('keydown', (event) => {
      const reviewSlide = event.target.closest?.('[data-cart-review-expand]');
      if (!reviewSlide || !['Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      event.stopPropagation();
      this.toggleReviewExpansion(reviewSlide);
    });

    this.reviewCarouselObserver = new MutationObserver(() => this.initializeReviewCarousel());
    this.reviewCarouselObserver.observe(this, { childList: true, subtree: true });
    this.initializeReviewCarousel();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  initializeReviewCarousel() {
    const carousel = this.querySelector('[data-cart-review-carousel]');
    if (!carousel) {
      this.stopReviewCarousel();
      return;
    }
    if (carousel.dataset.cartReviewInitialized === 'true') return;

    this.stopReviewCarousel();
    carousel.dataset.cartReviewInitialized = 'true';

    const slides = [...carousel.querySelectorAll('[data-cart-review-slide]')];
    const toggle = carousel.querySelector('[data-cart-review-toggle]');
    if (!slides.length) return;

    this.reviewCarouselIndex = 0;
    this.reviewCarouselPaused = false;
    this.showReviewSlide(slides, 0);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (slides.length < 2 || reducedMotion) {
      if (toggle) toggle.hidden = true;
      return;
    }

    carousel.addEventListener('mouseenter', () => this.stopReviewCarousel());
    carousel.addEventListener('mouseleave', () => {
      if (!this.reviewCarouselPaused) this.startReviewCarousel(carousel, slides);
    });
    carousel.addEventListener('focusin', () => this.stopReviewCarousel());
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget) && !this.reviewCarouselPaused) {
        this.startReviewCarousel(carousel, slides);
      }
    });

    this.startReviewCarousel(carousel, slides);
  }

  startReviewCarousel(carousel, slides) {
    if (!carousel || this.reviewCarouselPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.stopReviewCarousel();
    this.reviewCarouselTimer = window.setInterval(() => {
      this.reviewCarouselIndex = (this.reviewCarouselIndex + 1) % slides.length;
      this.showReviewSlide(slides, this.reviewCarouselIndex);
    }, 3000);
  }

  stopReviewCarousel() {
    if (!this.reviewCarouselTimer) return;
    window.clearInterval(this.reviewCarouselTimer);
    this.reviewCarouselTimer = null;
  }

  showReviewSlide(slides, activeIndex) {
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      if (!isActive) {
        slide.classList.remove('is-expanded');
        slide.setAttribute('aria-expanded', 'false');
      }
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  toggleReviewExpansion(slide) {
    const carousel = slide.closest('[data-cart-review-carousel]');
    const shouldExpand = !slide.classList.contains('is-expanded');

    carousel.querySelectorAll('[data-cart-review-expand]').forEach((reviewSlide) => {
      reviewSlide.classList.remove('is-expanded');
      reviewSlide.setAttribute('aria-expanded', 'false');
    });

    if (!shouldExpand) return;

    slide.classList.add('is-expanded');
    slide.setAttribute('aria-expanded', 'true');
    this.reviewCarouselPaused = true;
    this.stopReviewCarousel();

    const toggle = carousel.querySelector('[data-cart-review-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-pressed', 'true');
      toggle.setAttribute('aria-label', 'Play customer reviews');
      toggle.querySelector('[data-cart-review-pause]').hidden = true;
      toggle.querySelector('[data-cart-review-play]').hidden = false;
    }
  }

  toggleReviewCarousel(button) {
    const carousel = button.closest('[data-cart-review-carousel]');
    const slides = [...carousel.querySelectorAll('[data-cart-review-slide]')];

    this.reviewCarouselPaused = !this.reviewCarouselPaused;
    button.setAttribute('aria-pressed', this.reviewCarouselPaused ? 'true' : 'false');
    button.setAttribute('aria-label', this.reviewCarouselPaused ? 'Play customer reviews' : 'Pause customer reviews');
    button.querySelector('[data-cart-review-pause]').hidden = this.reviewCarouselPaused;
    button.querySelector('[data-cart-review-play]').hidden = !this.reviewCarouselPaused;

    if (this.reviewCarouselPaused) {
      this.stopReviewCarousel();
    } else {
      carousel.querySelectorAll('[data-cart-review-expand]').forEach((slide) => {
        slide.classList.remove('is-expanded');
        slide.setAttribute('aria-expanded', 'false');
      });
      this.startReviewCarousel(carousel, slides);
    }
  }

  addRecommendedProduct(event, button) {
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId;
    const originalText = button.textContent.trim();

    button.setAttribute('aria-disabled', true);
    button.setAttribute('aria-busy', true);
    button.textContent = 'Adding…';

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('items[0][id]', variantId);
    formData.append('items[0][quantity]', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || window.cartStrings?.error || 'Unable to add this bundle.');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendation',
          productVariantId: variantId,
          cartData: response,
        });
        this.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
        if (!button.isConnected) return;

        button.textContent = 'Try again';
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');

        window.setTimeout(() => {
          if (button.isConnected) button.textContent = originalText;
        }, 1600);
      });
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.initializeReviewCarousel();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
